import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import StandardModalContentNew from 'GeneralUI/StandardModalContent/StandardModalContentNew.jsx'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'
import withWindowScreenWorkflowConfigs from './withWindowScreenWorkflowConfigs.js'
import { getContextName, encodeItemURIComponents } from 'Helper/routeHelpers'
import InputGroupText from 'GeneralUI/inputGroupText/InputGroupText.jsx'
import { updateWindow, closeWindow } from 'actions/interactableActions'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { openModal } from 'actions/modalActions'
import { allWindows } from 'data/systemWindows'
import { xtractAPI } from 'actions/apiActions'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import _ from 'lodash'
import {
  percentToPx,
  pxToPercent,
  getErrorModalAction,
  windowConfigCleaner
} from './DesignerHelpers.jsx'

const EditWindowModal = ({
  windowConfig = {
    color: {
      header: '#DEDCFF',
      footer: '#DEDCFF'
    }
  },
  closeSelf,
  startSpinner,
  endSpinnerWithSuccess,
  endSpinnerWithError,
  contextWindowConfigs,
  contextWindowConfigId,
  contextScreenConfigs,
  contextScreensConfigsId
}) => {

  const colors = [
    '#E89829',
    '#F0BD76',
    '#4B9FBF',
    '#9CC',
    '#8D4C65',
    '#AC7C8F',
    '#5E8C61',
    '#A3B88A',
    '#3B1968',
    '#502D7F',
    '#754DAA',
    '#D5B5FF',
    '#DEDCFF'
  ]

  const dispatch = useDispatch()

  const [screenSwitchState, setScreenSwitchState] = useState(false)

  const { pathname } = useLocation()
  const currentScreenPath = useSelector(state => state.router?.match?.params?.screen)

  const [titleState, setTitleState] = useState(windowConfig?.name || "")
  const [windowContentState, setWindowContentState] = useState(windowConfig?.ref || "")
  const [topState, setTopState] = useState(y)
  const [leftState, setLeftState] = useState(x)
  const [widthState, setWidthState] = useState(width)
  const [heightState, setHeightState] = useState(height)
  const [settingsComponentState, setSettingsComponentState] = useState({})
  const [headerColorState, setHeaderColorState] = useState(windowConfig?.color?.header || "#DEDCFF")
  const [footerColorState, setFooterColorState] = useState(windowConfig?.color?.footer || "#DEDCFF")
  const [SettingsComponent, setSettingsComponent] = useState()

  const currentlyVisibleWindows = useSelector(state => state.interactableState.windows)
  const container = useSelector(state => state.interactableState.container)

  const currentScreenType = useMemo(getCurrentScreenType,[])

  const currentScreen = useMemo(() => {
    return contextScreenConfigs.find(screen => (screen.screenType === currentScreenType) && screen.path === currentScreenPath)
  }, [currentScreenPath, contextScreenConfigs, currentScreenType])

  const inUseWindows = useMemo(() => {
    let windows = screenSwitchState ? (currentScreen?.windows ? currentScreen.windows : []) : contextWindowConfigs
    return windows.map(window => window.screenType ? window : {...window, screenType: 'standard'})
      .filter(window => window.screenType === currentScreenType)
  }, [currentScreen, screenSwitchState])

  const isEdit = useMemo(() => {
    return inUseWindows.some(win => win.ref === windowContentState)
  }, [inUseWindows])

  const { x, y, width, height } = useMemo(() => {
    const onScreenWindow = currentlyVisibleWindows[windowConfig.ref]
    if (!onScreenWindow) {
      // this means we are creating a new window so return the default config
      return {
        x: 17,
        y: 17,
        width: 66,
        height: 66
      }
    }
    const y = pxToPercent(
      onScreenWindow.y,
      container.verticalOffset,
      container.height
    )
    const x = pxToPercent(
      onScreenWindow.x,
      container.horizontalOffset,
      container.width
    )

    const width = Math.round(
      (100 * onScreenWindow.width) /
      (container.width - container.horizontalOffset)
    )
    const height = Math.round(
      (100 * onScreenWindow.height) /
      (container.height - container.verticalOffset)
    )

    return { x, y, width, height }
  }, [currentlyVisibleWindows, container, windowConfig, pxToPercent])

  // causes values in the modal to reflect the actual current values of the window
  useEffect(() => {
    setLeftState(x)
  }, [x])

  useEffect(() => {
    setTopState(y)
  }, [y])

  useEffect(() => {
    setWidthState(width)
  }, [width])

  useEffect(() => {
    setHeightState(height)
  }, [height])

  const screenContext = useMemo(() => {
    return getContextName(pathname)
  }, [pathname])

  // load initial configurations
  useEffect(() => {
    // make api call to ensure window and screen data loads
    const searchObj = { or: [{ equal: { name: 'windows' } }, { equal: { name: 'screens' } }] }
    dispatch(xtractAPI('config', 'search', searchObj))
  }, [])

  // This is responsible for loading the custom windowConfig component with settings that are unique to this particular window
  useEffect(() => {
    const defaultWindowConfig = allWindows.find((defaultWindowConfig) => defaultWindowConfig.id === windowContentState)
    const contextConfig = (defaultWindowConfig?.contexts || []).find(conf => conf.context === screenContext)
    const loader = contextConfig?.loader ? contextConfig.loader : defaultWindowConfig?.loader
    if (!!defaultWindowConfig && loader != null) {
      // load the window
      loader().then((component) => {
        if (component.settingsConfig) {
          setSettingsComponent(() => component.settingsConfig)
        } else { // this component doesn't have a custom settingsComponent
          setSettingsComponent(null)
        }
      })
    } else if (SettingsComponent != null) {
      setSettingsComponent(null)
    }
  }, [allWindows, windowContentState])

  const availableWindowRefs = useMemo(() => {
    let availableWindowRefs = allWindows
      // note, these are coming from system window definitions where screenTypes is an array of possible screen types supported
      .map(window => window.screenTypes ? window : {...window, screenTypes: ['standard']})
      .filter(window => window.screenTypes.includes(currentScreenType))
      .filter(window => window.isConfigureable !== false)
      .filter(window => {
        const supportedContexts = window.contexts.map(contextConfig => contextConfig.context) // array of context names.
        return supportedContexts.includes(screenContext)
      })
      .map(window => window.id)
      .sort()

    // Then we look at the availableWindowRefs array and filter out any refs that are found in the inUseWindows array
    // (if !.some windows in the inUseArray match the ref in question it passes the filter).
    return availableWindowRefs.filter(possibleWindowRef => !inUseWindows.some(inUseWindow => inUseWindow.ref === possibleWindowRef))
  }, [allWindows, windowConfig?.ref, contextWindowConfigs, screenSwitchState, currentScreen, currentScreenType, inUseWindows])

  // this gets passed to a bunch of random components that expect to have a dispatchable
  // version of xtractAPI.
  const passableXtractAPI = useCallback((...args) => {
    dispatch(xtractAPI(...args))
  }, [])

  // this attempts to intelligently guess whether the window the user is trying to edit is a window config related to the currently
  // active "screen" or if its the "standalone" window config.
  useEffect(() => {
    if (currentScreen) {
      if (
        currentScreen?.windows &&
        currentScreen.windows.some(win => win.ref === windowContentState)
      ) {
        // we are on a screen path so the window could apply to the current screen. See if it is already configured for the current screen,
        // if it is we can begin by assuming that is where they want to edit it.
        setScreenSwitchState(true)
        return
      } else if (!windowConfig.ref) {
        // we didn't open with a windowConfig.ref meaning we didn't open editing a specific window so there is good chance we are trying
        // to add a window and if there is a screen open, we are probably trying to add the window to a screen.
        setScreenSwitchState(true)
        return
      }
      // if the current windowContentState isn't a ref in the current screen, either no current contentState is selected yet (meaning we arent
      // editing a screens window) or the window being edited isn't part of the current screen
      setScreenSwitchState(false)
    } else {
      // it cant be related to a screen since we don't have a screen open
      setScreenSwitchState(false)
    }
  }, [currentScreen])

  const windowConfigTypeSwitch = useCallback(() => {
    const possibleScreen = currentScreenPath && currentScreenPath !== ''
    let screenName = ''
    if (possibleScreen) {
      screenName = (currentScreen || { name: '' }).name
    }
    const onLabel = screenName === '' ? 'Screen Window' : `${screenName} Screen Window`

    return (
      <div className='window-type-input'>
        <InputGroupText
          type="select"
          tabIndex={0}
          title="Apply Edits To"
          aria-label={screenSwitchState ? `apply changes to ${onLabel}` : 'apply changes to standalone window'}
          value={screenSwitchState ? 1 : 0}
          disabled={!possibleScreen}
          options={[{ label: 'Standalone Window', value: 0 }, { label: onLabel, value: 1 }]}
          onChange={e => {setScreenSwitchState(e.value === 1)}}
        />
      </div>
    )
  }, [currentScreenPath, screenSwitchState, currentScreen, setScreenSwitchState])

  // this is the dropdown menu of windows to be selected from
  const windowContent = useCallback(() => {
    const options = availableWindowRefs.map(w => { return { label: w, value: w }})
    if(windowConfig.ref) {
      // if we are editing a window, its already configured so it isn't in the list of available
      // refs but it should be shown in the option menu so add it.
      options.push({label: windowConfig.ref, value: windowConfig.ref})
    }
    return (
      <div className='content-input'>
        <InputGroupText
          tabIndex={2}
          type="select"
          title="Window Content"
          aria-label={'window content'}
          value={windowContentState}
          disabled={windowConfig.ref ? true : false}
          options={options}
          onChange={e => setWindowContentState(e.value)}
        />
      </div>
    )
  }, [windowConfig.ref, availableWindowRefs, windowContentState, setWindowContentState, currentScreenPath])

  // color controls for the window
  const showColorOption = useCallback((type) => {
    const color = {
      header: headerColorState,
      footer: footerColorState
    }
    return (
      <React.Fragment>
        {type + ' color:'}
        <div className="color-dropdown">
          <div
            className={'color-circle'}
            style={{ backgroundColor: color[type] || '#DEDCFF' }}
            aria-label={'color button'}
          />
          <div className="color-dropdown-menu">
            {colors.map((colorCode) => {
              return (
                <div
                  key={colorCode}
                  className={'color-circle'}
                  aria-label={`${colorCode} link`}
                  style={{ backgroundColor: colorCode }}
                  onClick={() => type == 'header' ? setHeaderColorState(colorCode) : setFooterColorState(colorCode)}
                />
              )
            })}
          </div>
        </div>
      </React.Fragment>
    )
  }, [headerColorState, footerColorState, setHeaderColorState, setFooterColorState])

  const isFormValid = useCallback(() => {
    if (!titleState) {
      dispatch(getErrorModalAction('Missing Title', 'Every window must have a title defined'))
      return false
    }

    if (!windowContentState) {
      dispatch(getErrorModalAction('Missing Window Content', 'Every window must have a window content defined'))
      return false
    }

    return true
  }, [titleState, windowContentState, getErrorModalAction])

  // updates the properties of an existing window config
  const updateWindowProperties = useCallback((window) => {
    const newWindow = _.cloneDeep(window)
    return {
      ...newWindow,
      ...settingsComponentState,
      x: leftState + '%',
      y: topState + '%',
      width: widthState + '%',
      height: heightState + '%',
      name: titleState,
      screenType: currentScreenType,
      color: {
        header: headerColorState,
        footer: footerColorState
      }
    }
  }, [titleState, leftState, topState, widthState, heightState, headerColorState, footerColorState, currentScreenType, settingsComponentState])

  // generates a new windowConfig object based on current UI inputs
  const getNewWindow = useCallback(() => {
    const defaultConfig = allWindows.find(win => win.id === windowContentState)?.windowConfig || {}
    // set initial properties
    const newWindow = {
      ...defaultConfig,
      key: '',
      selectable: true,
      ref: windowContentState,
    }
    // have updateWindow do the rest of the work
    return updateWindowProperties(newWindow)
  }, [windowContentState, updateWindowProperties, allWindows])

  // gents a new window based on UI inputs (getNewWindow)
  // then updates the visible window on the UI (updateWindowOnScreen)
  // then adds the window to the array of windows and updates state
  // then makes an api call to save the update windows array (saveWindowsToApi)
  const createSingularWindow = useCallback(() => {
    if (!isFormValid()) {
      return true // leave modal open
    }

    let allWindows = _.cloneDeep(contextWindowConfigs)
    const newWindow = getNewWindow()
    allWindows = [...allWindows, newWindow]
    updateWindowOnScreen(newWindow)
    saveWindowsToApi(allWindows)
  }, [contextWindowConfigs, isFormValid, getNewWindow, saveWindowsToApi, updateWindowOnScreen])

  // finds the screen matching the current screenPath out of all possible screens
  // finds the referenced window within that screen
  //     if found, updates its properties (updateWindowProperties)
  //     if not found, its config is created (getNewWindow) and added to the screen config
  // the state screen configuration is then updated with the change
  // the window is then updated on the UI (updateWindowOnScreen)
  // the screen configuration is then saved to the API (saveScreensToApi)
  const updateOrCreateScreenWindow = useCallback(() => {
    if (!isFormValid()) {
      return true // leave modal open
    }

    let allScreens = _.cloneDeep(contextScreenConfigs)
    let newWindow
    allScreens = allScreens.map(s => {
      if (s.path === currentScreenPath && s.screenType === currentScreenType) {
        if (s.windows && s.windows.length)
          s.windows = s.windows.map(w => {
            if (w.ref === windowConfig.ref) {
              newWindow = updateWindowProperties(w)
              return newWindow
            } else return w
          })

        if (!newWindow) {
          newWindow = getNewWindow()
          if (s.windows && Array.isArray(s.windows))
            s.windows.push(newWindow)
          else if (!s.windows)
            s['windows'] = [newWindow]
        }
      }
      return {
        ...s,
        windows: windowConfigCleaner(s.windows, availableWindowRefs)
      }
    })

    if (newWindow) {
      updateWindowOnScreen(newWindow)
      saveScreensToApi(allScreens)
    }
  }, [contextScreenConfigs, currentScreenType, isFormValid, updateWindowProperties, getNewWindow, saveScreensToApi, availableWindowRefs, windowConfig?.ref])

  // deletes the window from the current screen and then saves to API
  const deleteWindow = useCallback(() => {
    if (screenSwitchState) {
      // delete window from current screen
      let allScreens = _.cloneDeep(contextScreenConfigs)
      allScreens = allScreens.map(screen => {
        if (screen.path === currentScreenPath && (screen.screenType === currentScreenType || (!screen.screenType && currentScreenType === 'standard')) && screen.windows) {
          screen.windows = screen.windows.filter(w => w.ref !== windowContentState)
        }
        return screen
      })
      saveScreensToApi(allScreens)
    } else {
      // delete window from possible windows
      let allWindows = _.cloneDeep(contextWindowConfigs)
      allWindows = allWindows.filter(win => !(win.ref === windowContentState && win.screenType === currentScreenType))
      saveWindowsToApi(allWindows)
    }
    dispatch(closeWindow(windowContentState))
  }, [contextScreenConfigs, contextWindowConfigs, currentScreenPath, currentScreenType, windowContentState, windowConfig, screenSwitchState, closeWindow, saveWindowsToApi])

  const deleteWindowRef = useRef(deleteWindow)
  useEffect(() => {
    deleteWindowRef.current = deleteWindow
  }, [deleteWindow])

  const confirmDeleteModal = useCallback((windowName) => {
    dispatch(openModal('confirm_modal', {
      title: 'Delete Window',
      content: (
        <StandardModalContent
          messageContent={
            <div style={{ margin: '25px' }}>
              Are you sure you want to delete <b>{windowName ? windowName : ''} </b>window?
            </div>
          }
          cancelText={'No'}
          cancelHandler={() => false}
          okText={'Yes'}
          okHandler={deleteWindowRef.current}
        />
      ),
      closeable: true
    }))
    return true //leave modal open
  }, [deleteWindowRef])

  // determins if we should edit a window in a screen, create a window in a screen, create a window outside of a screen
  const saveHandler = useCallback(() => {
    // Note: all handlers below return true/false to determine if modal should remain open or not after click action
    if (screenSwitchState) {
      return updateOrCreateScreenWindow()
    } else if (!isEdit) {
      return createSingularWindow()
    } else {
      return updateSingularWindow(windowConfig)
    }
  }, [isEdit, windowConfig, screenSwitchState, createSingularWindow, currentScreenPath, updateSingularWindow, updateOrCreateScreenWindow])

  // updates the UI to reflect window changes
  const updateWindowOnScreen = useCallback((newWindow) => {
    if (currentlyVisibleWindows[newWindow.ref]) {
      // the window is already open so we need to update it
      const cleanedWidth = newWindow.width.replace("%", "")
      const cleanedHeight = newWindow.height.replace("%", "")
      const updatedWindow = {
        ...newWindow,
        x: percentToPx(newWindow.x, container.horizontalOffset, container.width),
        y: percentToPx(newWindow.y, container.verticalOffset, container.height),
        width: Math.round((cleanedWidth / 100.0) * (container.width - container.horizontalOffset)),
        height: Math.round((cleanedHeight / 100.0) * (container.height - container.verticalOffset)),
      }

      dispatch(updateWindow(updatedWindow))
    } else {
      // the window isn't open so we need to update it. Since its opening for the first time, we don't need to do
      // all of the percent to pixel conversion since that will happen automatically
      dispatch(updateWindow(newWindow))
    }
  }, [percentToPx, updateWindow, currentlyVisibleWindows])

  // gets the updated version of the window with the new input values (updateWindowProperties)
  // finds the window in the global list of windows and replaces it
  // updates the window in the UI (updateWindowOnScreen)
  // saves the windows to the api
  const updateSingularWindow = useCallback((winConfig) => {
    const newWindow = updateWindowProperties(winConfig)

    // replace the old version of the window in the windows array with the new version
    const allWindows = contextWindowConfigs.map(win => win.ref === newWindow.ref && win.screenType == currentScreenType ? newWindow : win)

    updateWindowOnScreen(newWindow)
    saveWindowsToApi(allWindows)
  }, [container, contextWindowConfigs, currentScreenType, saveWindowsToApi, updateWindowProperties, updateWindowOnScreen])

  const saveWindowsToApi = useCallback((windows) => {
    const windowData = JSON.stringify(windowConfigCleaner(windows, availableWindowRefs))
    const callbacks = {
      200: response => {
        endSpinnerWithSuccess()
      },
      400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors }),
      error: (response, action) => endSpinnerWithError({ response, action })
    }
    startSpinner()
    const config = {
      name: 'windows',
      section: screenContext,
      value: windowData,
      app: 'XIS'
    }

    if (contextWindowConfigId) {
      // update the window config
      dispatch(xtractAPI(`config/${contextWindowConfigId}`, 'update', config, callbacks))
    } else {
      // no window config exists so create a new one
      dispatch(xtractAPI('config', 'create', config, callbacks))
    }
  },[contextWindowConfigId, startSpinner, endSpinnerWithError, endSpinnerWithSuccess, availableWindowRefs, encodeItemURIComponents, screenContext])

  const saveScreensToApi = useCallback((screens) => {
    const screenData = JSON.stringify(screens.map(encodeItemURIComponents))
    const callbacks = {
      200: response => {
        endSpinnerWithSuccess()
      },
      400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors }),
      error: (response, action) => endSpinnerWithError({ response, action })
    }
    startSpinner()

    const config = {
      name: 'screens',
      section: screenContext,
      value: screenData,
      app: 'XIS'
    }

    if (contextScreensConfigsId) {
      // update the screen config
      dispatch(xtractAPI(`config/${contextScreensConfigsId}`, 'update', config, callbacks))
    } else {
      // no screen config exists so create a new one
      dispatch(xtractAPI('config', 'create', config, callbacks))
    }
  }, [contextScreensConfigsId, startSpinner, endSpinnerWithError, endSpinnerWithSuccess, encodeItemURIComponents, screenContext])

  const deleteHandler = useMemo(() => {
    return isEdit ? () => confirmDeleteModal(windowContentState) : null
  }, [confirmDeleteModal, windowContentState, isEdit])

  const settingsComponentUpdate = useCallback((updatedWindowConfig) => {
    // we don't know what properties the settingsComponent may be creating or setting so we have to
    // extract all of the properties that we do know about from the updatedWindowConfig and whatever
    // is left over, belongs to the settingsComponent
    const {ref, title, color, x, y, width, height, ...rest} = updatedWindowConfig
    setSettingsComponentState(rest)
  }, [])

  return (
    <StandardModalContentNew
      cancelHandler={closeSelf}
      okHandler={deleteHandler}
      thirdHandler={saveHandler}
      okText={'Delete'}
      okClassName={'btn-warning'}
      thirdText={'Save'}
      cancelText={'Cancel'}
      closeSelf={closeSelf}
    >
      <div className='edit-window-modal' style={{ justifyContent: SettingsComponent ? 'space-around' : 'center' }}>
        <div className='config'>
          {windowConfigTypeSwitch()}
          <div className='title-input'>
            <InputGroupText
              tabIndex={1}
              type="text"
              placeholder=""
              title="Window Title"
              aria-label={'window title'}
              value={titleState}
              style={{ textAlign: 'left' }}
              onChange={e => setTitleState(e.target.value)}
            />
          </div>
          {windowContent()}
          <div className='config-row'>
            <div className='config-col'>
              <div className='percent-input'>
                <InputGroupText
                  tabIndex={3}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  precision={0}
                  title="Width"
                  placeholder="0"
                  aria-label={'window width'}
                  value={widthState}
                  style={{ textAlign: 'center' }}
                  onChange={e => setWidthState(parseInt(e.target.value))}
                />
                %
              </div>
              <div className='percent-input'>
                <InputGroupText
                  tabIndex={5}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  precision={0}
                  title="Top"
                  placeholder="0"
                  aria-label={'window top position'}
                  value={topState}
                  style={{ textAlign: 'center' }}
                  onChange={e => setTopState(parseInt(e.target.value))}
                />
                %
              </div>
              <div className="window-colors">
                <div className='header'>{showColorOption('header')}</div>
              </div>
            </div>
            <div className='config-col'>
              <div className='percent-input'>
                <InputGroupText
                  tabIndex={4}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  precision={0}
                  title="Height"
                  placeholder="0"
                  aria-label={'window height position'}
                  value={heightState}
                  style={{ textAlign: 'center' }}
                  onChange={e => setHeightState(parseInt(e.target.value))}
                />
                %
              </div>
              <div className='percent-input'>
                <InputGroupText
                  tabIndex={6}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  precision={0}
                  title="Left"
                  placeholder="0"
                  aria-label={'window left position'}
                  value={leftState}
                  style={{ textAlign: 'center' }}
                  onChange={e => setLeftState(parseInt(e.target.value))}
                />
                %
              </div>
              <div className="window-colors">
                <div className='footer'>{showColorOption('footer')}</div>
              </div>
            </div>
          </div>
        </div>
        <div className='settings' style={{ display: SettingsComponent ? 'block' : 'none' }}>
          {
            SettingsComponent
              ? <SettingsComponent
                windowConfig={updateWindowProperties(windowConfig)}
                updateCallback={settingsComponentUpdate}
                windowContext={screenContext}
                xtractAPI={passableXtractAPI}
              />
              : null
          }
        </div>
      </div>
    </StandardModalContentNew>
  )
}

EditWindowModal.propTypes = {
  closeSelf: PropTypes.func.isRequired,
}

const enhance = compose(
  withWindowScreenWorkflowConfigs()
)

export default enhance(EditWindowModal)
